import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "container",
        "step",
        "progressStep",
        "backBtn",
        "nextBtn",
        "submitBtn",
        "termsCheckbox",
        "form",
        "notice"
    ]

    static values = {
        bodyOpenClass: { type: String, default: "multistep-modal-opened" }
    }

    connect() {
        this.currentStep = 1
        this.totalSteps = this.stepTargets.length
        this.stepValues = {}
        this.noticeTimeout = null

        this.initialButtonStates = new WeakMap()
        this.resettableButtons = [
            ...this.nextBtnTargets,
            ...this.submitBtnTargets
        ]

        this.resettableButtons.forEach((btn) => {
            this.initialButtonStates.set(btn, this.captureButtonState(btn))
        })

        this.previousBodyOverflow = ""
    }

    open() {
        this.reset()
        this.containerTarget.setAttribute("aria-hidden", "false")

        this.previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"
        document.body.classList.add(this.bodyOpenClassValue)

        this.updateProgress()
    }

    close() {
        this.containerTarget.setAttribute("aria-hidden", "true")

        document.body.style.overflow = this.previousBodyOverflow
        document.body.classList.remove(this.bodyOpenClassValue)

        this.reset()
    }

    disconnect() {
        document.body.classList.remove(this.bodyOpenClassValue)
        document.body.style.overflow = this.previousBodyOverflow || ""
    }

    next() {
        if (this.currentStep < this.totalSteps) {
            this.currentStep++
            this.updateSteps()
            this.updateProgress()
            this.scrollContentToTop()
        }
    }

    back() {
        if (this.currentStep > 1) {
            this.currentStep--
            this.updateSteps()
            this.updateProgress()
        } else {
            this.close()
        }
    }

    handleClick(event) {
        const button = event.currentTarget

        if (this.isDisabled(button)) {
            event.preventDefault()
            this.showNotice()
            return
        }

        this.next()
    }

    selectStepValue(event) {
        const clickedElement = event.currentTarget
        const stepValue = clickedElement.dataset.stepValue
        const currentStepElement = this.getCurrentStepElement()
        const fieldName = currentStepElement?.dataset.stepField

        if (!fieldName || !stepValue) return

        this.hideNotice()

        const selectableItems = currentStepElement.querySelectorAll("[data-step-value]")
        selectableItems.forEach((item) => {
            if (item.tagName !== "INPUT") {
                item.classList.remove("character-card--selected")
            }
        })

        if (clickedElement.tagName !== "INPUT") {
            clickedElement.classList.add("character-card--selected")
        }

        this.setStepValue(fieldName, stepValue)

        currentStepElement
            .querySelectorAll('[data-multistep-modal-target~="nextBtn"], [data-multistep-modal-target~="submitBtn"]')
            .forEach((btn) => this.setDisabled(btn, false))
    }

    submit(event) {
        event.preventDefault()

        const button = event.currentTarget
        if (this.isDisabled(button)) {
            this.showNotice()
            return
        }

        const currentStepElement = this.getCurrentStepElement()
        const fieldName = currentStepElement?.dataset.stepField

        if (fieldName && !this.stepValues[fieldName]) {
            const submitValue = button.dataset.submitValue || "1"
            this.setStepValue(fieldName, submitValue)
        }

        if (this.hasFormTarget) {
            this.formTarget.requestSubmit()
        }
    }

    setStepValue(fieldName, value) {
        this.stepValues[fieldName] = value
        this.updateHiddenInput(fieldName, value)
    }

    removeStepValue(fieldName) {
        delete this.stepValues[fieldName]
        this.removeHiddenInput(fieldName)
    }

    updateHiddenInput(name, value) {
        if (!this.hasFormTarget) return

        let input = this.formTarget.querySelector(`input[name="${name}"]`)

        if (!input) {
            input = document.createElement("input")
            input.type = "hidden"
            input.name = name
            this.formTarget.appendChild(input)
        }

        input.value = value
    }

    removeHiddenInput(name) {
        if (!this.hasFormTarget) return

        const input = this.formTarget.querySelector(`input[name="${name}"]`)
        if (input) input.remove()
    }

    getCurrentStepElement() {
        return this.stepTargets.find(
            (step) => parseInt(step.dataset.step, 10) === this.currentStep
        )
    }

    updateSteps() {
        this.stepTargets.forEach((step, index) => {
            step.classList.toggle("step--active", index + 1 === this.currentStep)
        })
    }

    updateProgress() {
        this.progressStepTargets.forEach((progressStep) => {
            const stepNumber = parseInt(progressStep.dataset.stepNumber, 10)

            if (stepNumber < this.currentStep) {
                progressStep.classList.add("progress-steps__item--completed")
                progressStep.classList.remove("progress-steps__item--active")
            } else if (stepNumber === this.currentStep) {
                progressStep.classList.add("progress-steps__item--active")
                progressStep.classList.remove("progress-steps__item--completed")
            } else {
                progressStep.classList.remove(
                    "progress-steps__item--active",
                    "progress-steps__item--completed"
                )
            }
        })
    }

    scrollContentToTop() {
        const modalContent = this.element.querySelector(".multistep-modal__content")
        if (!modalContent) return

        modalContent.scrollTo({
            top: 0,
            behavior: "smooth"
        })
    }

    showNotice() {
        if (!this.hasNoticeTarget) return

        this.noticeTarget.classList.remove("d-none")

        const modalContent = this.element.querySelector(".multistep-modal__content")
        if (modalContent) {
            setTimeout(() => {
                modalContent.scrollTo({
                    top: modalContent.scrollHeight,
                    behavior: "smooth"
                })
            }, 50)
        }

        if (this.noticeTimeout) clearTimeout(this.noticeTimeout)
        this.noticeTimeout = setTimeout(() => {
            this.hideNotice()
        }, 5000)
    }

    hideNotice() {
        if (!this.hasNoticeTarget) return
        this.noticeTarget.classList.add("d-none")
    }

    captureButtonState(button) {
        return {
            disabled: button.disabled,
            hasAriaDisabled: button.hasAttribute("aria-disabled"),
            ariaDisabled: button.getAttribute("aria-disabled")
        }
    }

    restoreButtonState(button, state) {
        if (!button || !state) return

        button.disabled = !!state.disabled

        if (state.hasAriaDisabled) {
            button.setAttribute("aria-disabled", state.ariaDisabled ?? "false")
        } else {
            button.removeAttribute("aria-disabled")
        }
    }

    reset() {
        this.currentStep = 1
        this.stepValues = {}

        if (this.noticeTimeout) {
            clearTimeout(this.noticeTimeout)
            this.noticeTimeout = null
        }

        this.hideNotice()

        if (this.hasFormTarget) {
            this.formTarget.innerHTML = ""
        }

        this.element.querySelectorAll(".character-card--selected").forEach((card) => {
            card.classList.remove("character-card--selected")
        })

        this.element.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
            checkbox.checked = false
        })

        this.element.querySelectorAll('input[type="text"], input[type="email"], textarea').forEach((input) => {
            input.value = ""
        })

        this.resettableButtons.forEach((btn) => {
            this.restoreButtonState(btn, this.initialButtonStates.get(btn))
        })

        this.updateSteps()
        this.updateProgress()
    }

    isDisabled(element) {
        if (!element) return false
        return element.getAttribute("aria-disabled") === "true" || element.disabled === true
    }

    setDisabled(element, disabled) {
        if (!element) return
        element.setAttribute("aria-disabled", disabled ? "true" : "false")
        element.disabled = !!disabled
    }
}
